import type {
    ProductExportQuery,
    ProductListQuery,
} from '@/api/handlers/resources/products';
import {
    PRODUCT_DEFAULT_PAGE_SIZE,
    PRODUCT_PAGE_SIZE_OPTIONS,
} from './productsMeta';
import {
    PRODUCT_FILTER_DESCRIPTORS,
    PRODUCT_FILTER_DESCRIPTOR_LIST,
    PRODUCT_RANGE_DESCRIPTORS,
    collectProductFilterFields,
} from './productFilterDescriptors';
import {
    isProductListRangeInverted,
    type ProductFilterGroup,
    type ProductListDrawerFilters,
    type ProductListFilters,
    type ProductListRange,
    type ProductRangeFilter,
} from './productFilterTypes';

export {
    OPEN_PRODUCT_LIST_RANGE,
    PRODUCT_RANGE_ENDS,
    isProductListRangeInverted,
} from './productFilterTypes';
export type {
    ProductFilterGroup,
    ProductListDrawerFilters,
    ProductListFilters,
    ProductListRange,
    ProductRangeEnd,
    ProductRangeFilter,
} from './productFilterTypes';

/** Derived in drawer order, also used for the tags and the badge. */
export const PRODUCT_FILTER_GROUPS: readonly ProductFilterGroup[] =
    PRODUCT_FILTER_DESCRIPTOR_LIST.map(({ group }) => group);

export const PRODUCT_RANGE_FILTERS: readonly ProductRangeFilter[] =
    PRODUCT_RANGE_DESCRIPTORS.map(({ group }) => group);

export const DEFAULT_PRODUCT_LIST_DRAWER_FILTERS: Readonly<ProductListDrawerFilters> =
    Object.freeze(collectProductFilterFields(({ cleared }) => cleared));

export const DEFAULT_PRODUCT_LIST_FILTERS: Readonly<ProductListFilters> =
    Object.freeze({
        ...DEFAULT_PRODUCT_LIST_DRAWER_FILTERS,
        search: '',
        page: 1,
        pageSize: PRODUCT_DEFAULT_PAGE_SIZE,
    });

/** Search and paging are not drawer filter groups. */
const PARAM = { search: 'search', page: 'page', pageSize: 'pageSize' } as const;
const ADDRESS_DESCRIPTORS = [...PRODUCT_FILTER_DESCRIPTOR_LIST].sort(
    (left, right) => left.addressOrder - right.addressOrder,
);

/** The last page before the API's 32-bit skip count overflows. */
const MAX_PAGE =
    Math.floor(2_147_483_647 / Math.max(...PRODUCT_PAGE_SIZE_OPTIONS)) + 1;

const readWholeNumber = (
    params: URLSearchParams,
    key: string,
): number | null => {
    const value = params.get(key);
    return value && /^\d+$/.test(value) ? Number(value) : null;
};

const readPage = (params: URLSearchParams): number => {
    const page = readWholeNumber(params, PARAM.page);
    return page !== null && page >= 1 && page <= MAX_PAGE
        ? page
        : DEFAULT_PRODUCT_LIST_FILTERS.page;
};

const readPageSize = (params: URLSearchParams): number => {
    const size = readWholeNumber(params, PARAM.pageSize);
    return size !== null && PRODUCT_PAGE_SIZE_OPTIONS.includes(size)
        ? size
        : DEFAULT_PRODUCT_LIST_FILTERS.pageSize;
};

/** Validate each group's address values before they reach requests. */
export const readProductListFilters = (
    params: URLSearchParams,
): ProductListFilters => ({
    ...collectProductFilterFields((descriptor) => descriptor.read(params)),
    search: params.get(PARAM.search)?.trim() ?? '',
    page: readPage(params),
    pageSize: readPageSize(params),
});

/** Defaults are omitted; unrelated address keys and established order survive. */
export const writeProductListFilters = (
    params: URLSearchParams,
    filters: ProductListFilters,
): URLSearchParams => {
    const next = new URLSearchParams(params);
    Object.values(PARAM).forEach((key) => next.delete(key));
    ADDRESS_DESCRIPTORS.forEach(({ addressKeys }) =>
        addressKeys.forEach((key) => next.delete(key)),
    );

    const search = filters.search.trim();
    if (search) {
        next.set(PARAM.search, search);
    }
    ADDRESS_DESCRIPTORS.forEach((descriptor) =>
        descriptor.write(next, filters),
    );
    if (filters.page !== DEFAULT_PRODUCT_LIST_FILTERS.page) {
        next.set(PARAM.page, String(filters.page));
    }
    if (filters.pageSize !== DEFAULT_PRODUCT_LIST_FILTERS.pageSize) {
        next.set(PARAM.pageSize, String(filters.pageSize));
    }
    return next;
};

/** The same descriptor fields feed both endpoints; export has no paging. */
export const toProductExportQuery = (
    filters: ProductListFilters,
): ProductExportQuery =>
    ADDRESS_DESCRIPTORS.reduce<ProductExportQuery>(
        (query, descriptor) => ({ ...query, ...descriptor.toRequest(filters) }),
        filters.search ? { search: filters.search } : {},
    );

export const toProductListQuery = (
    filters: ProductListFilters,
): ProductListQuery => ({
    page: filters.page,
    pageSize: filters.pageSize,
    ...toProductExportQuery(filters),
});

/** Pick only fields owned by descriptors, leaving out search and paging. */
export const pickProductListDrawerFilters = (
    filters: ProductListDrawerFilters,
): ProductListDrawerFilters =>
    collectProductFilterFields(({ cleared }) => {
        const fields: Partial<ProductListDrawerFilters> = {};
        // These keys belong to our typed registry, never to external input.
        const keys = Object.keys(cleared) as (keyof ProductListDrawerFilters)[];
        keys.forEach((key) => Object.assign(fields, { [key]: filters[key] }));
        return fields;
    });

export const getInvertedProductListRanges = (
    filters: ProductListDrawerFilters,
): ProductRangeFilter[] =>
    PRODUCT_RANGE_FILTERS.filter((filter) =>
        isProductListRangeInverted(filters[filter]),
    );

export const getActiveProductFilterGroups = (
    filters: ProductListDrawerFilters,
): ProductFilterGroup[] =>
    PRODUCT_FILTER_DESCRIPTOR_LIST.filter((descriptor) =>
        descriptor.isActive(filters),
    ).map(({ group }) => group);

/** The patch that sets one range, leaving the rest. */
export const toRangePatch = (
    filter: ProductRangeFilter,
    range: ProductListRange,
): Partial<ProductListDrawerFilters> => ({ [filter]: range });

export const clearProductFilterGroup = (
    group: ProductFilterGroup,
): Partial<ProductListDrawerFilters> =>
    PRODUCT_FILTER_DESCRIPTORS[group].cleared;

/** Branch changes figures, not rows; archived and search still count for Reset. */
export const hasActiveProductListFilters = (
    filters: ProductListFilters,
): boolean =>
    filters.search.length > 0 ||
    PRODUCT_FILTER_DESCRIPTOR_LIST.some(
        (descriptor) => descriptor.affectsRows && descriptor.isActive(filters),
    );
